import React from "react";
import Radium from "radium";
import { RaisedButton, FlatButton, Dialog, TextField } from "material-ui";
import { Link } from "react-router";
import moment from "moment";
import chunk from "lodash/chunk";
import { Table } from "reactable";

import api from "../utils/api";
import config from "../config";
import UserStore from "../stores/UserStore";
import EventStore from "../stores/EventStore";
import BallotActions from "../actions/BallotActions";

import BaseOverlay from "./partials/overlays/base.jsx";
import InjectedSelect from "./partials/injected-select.jsx";
import LinkLike from "./partials/link-like.jsx";
import InfoNotice from "./partials/info-notice.jsx";

const generateGrid = function (event) {
  function getChunkSize(count) {
    if (count <= 10) {
      return 2;
    } else if (count <= 21) {
      return 3;
    } else {
      return 4;
    }
  }

  let { films } = event;

  // Blocks of 3+ - hide unballoted if same as event name
  if (films.length >= 3) {
    films = films.filter(f =>
      f.name === event.name && f.unballoted ? false : true
    );
  }

  if (films.length <= 2) {
    return films.map(f => [f]);
  }

  let chunkSize = getChunkSize(films.length),
    filmChunks = chunk(films, chunkSize);

  // Transform the film matrix to vertical columns
  // [[1,2],       [[1,4],
  //  [3,4],  ==>   [2,5]
  //  [5]]          [3]]
  let newChunks = new Array(filmChunks.length).fill().map(() => []);
  films.forEach((f, i) => {
    newChunks[i % newChunks.length].push(f);
  });

  return newChunks;
};

class BallotingRecord extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      event: null,
      totalsShown: false,
      recording: false,
      submitting: false,
      currentSessionCount: 0,
      currentBallot: {},
      currentBallotNextFilm: null,
      currentBallotReadyToSubmit: false,
      currentSessionIsBulk: false
    };

    this.updateNextFilm = this.updateNextFilm.bind(this);
    this.updateVote = this.updateVote.bind(this);
    this.onKeypress = this.onKeypress.bind(this);
    this.launch = this.launch.bind(this);
    this.close = this.close.bind(this);
    this.submitBallot = this.submitBallot.bind(this);
  }

  static contextTypes = {
    router: React.PropTypes.object
  };

  getStyles() {
    return {
      actions: {
        marginTop: "1.5em"
      },
      count: {
        fontSize: "0.6em",
        color: "rgba(33, 33, 33, 0.6)",
        margin: "-0.5em 0 -1em 0"
      },
      previewBallot: {
        root: {
          display: "table",
          border: 0,
          margin: "1em auto",
          textAlign: "center",
          position: "relative",
          borderCollapse: "collapse",
          width: "90%"
        },
        row: {
          display: "table-row"
        },
        submitting: {
          backgroundColor: "rgba(255,255,255,0.8)",
          display: "block",
          position: "absolute",
          top: 1,
          left: 0,
          right: 0,
          bottom: 0
        },
        entry: {
          border: "1px solid #ccc",
          padding: 20,
          display: "table-cell",
          verticalAlign: "middle"
        },
        name: {
          fontWeight: "bold",
          marginBottom: 5
        },
        ratingBubble: {
          backgroundColor: "rgba(33, 33, 33, 0.6)",
          borderRadius: "100%",
          color: "#fff",
          fontWeight: "bold",
          fontSize: "17pt",
          padding: 5,
          width: 30,
          height: 30,
          lineHeight: "30px",
          display: "inline-block",
          marginLeft: 5,
          cursor: "pointer",
          transition: "0.01s ease-out",
          ":hover": {
            transform: "scale(1.05)"
          }
        },
        ratingBubbleSelected: {
          backgroundColor: "rgb(2, 136, 209)"
        },
        ratingButtonContainer: {
          marginLeft: -5
        }
      },
      submit: {
        textAlign: "center"
      }
    };
  }

  submitBallot() {
    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });

    api.request(
      {
        method: "POST",
        endpoint: `ballots`,
        api_key: UserStore.getState().apiKey,
        json: {
          event: this.state.event.id,
          ballot: this.state.currentBallot,
          bulk: this.state.currentSessionIsBulk
        }
      },
      (err, d) => {
        if (err) {
          window.alert(err.message);
          this.setState({ submitting: false });
        } else {
          this.setState(
            {
              currentBallot: {},
              currentBallotReadyToSubmit: false,
              currentSessionCount:
                this.state.currentSessionCount + (d.ballots_recorded || 1),
              submitting: false
            },
            () => {
              if (this.state.currentSessionIsBulk) {
                this.close();
              }
              this.updateNextFilm();
            }
          );
        }
      }
    );
  }

  updateNextFilm() {
    const { event, currentBallot } = this.state,
      films = event.films.filter(f => !f.unballoted);

    if (!films.length) {
      this.setState({ currentBallotNextFilm: null });
      return;
    }

    for (let i = 0; i < films.length; i++) {
      if (!currentBallot[films[i].id]) {
        this.setState({
          currentBallotNextFilm: films[i].id,
          currentBallotReadyToSubmit: Object.values(currentBallot).length > 0
        });
        return;
      }
    }

    // leave the pointer at the previous entry
    this.setState({ currentBallotReadyToSubmit: true });
  }

  updateVote(id, vote) {
    let { currentBallot, currentSessionIsBulk } = this.state;
    if (currentSessionIsBulk) {
      if (!currentBallot[id]) {
        currentBallot[id] = {};
      }
      currentBallot[id][vote.score] = vote.count;
    } else {
      currentBallot[id] = vote;
    }
    this.setState({ currentBallot }, () => {
      this.updateNextFilm();
    });
  }

  onKeypress(e) {
    let labels = this.props.settings.rank_labels.split(",").map(l => l.trim()),
      vote = +e.key,
      next = this.state.currentBallotNextFilm;

    if (this.state.submitting) {
      // fall through
    } else if (this.state.currentBallotReadyToSubmit && e.key === "Enter") {
      this.submitBallot();
    } else if (next && vote > 0 && vote < labels.length + 1) {
      // numeric = worst->best
      if (labels.some(l => +l === 1)) {
        vote = labels.length - vote + 1;
      }

      this.updateVote(next, vote);
    }
  }

  launch(bulk) {
    if (bulk) {
      BallotActions.fetchVotes.defer(this.state.event.id);
    }

    if (this.state.recording) {
      return;
    } // already launched
    this.setState(
      {
        currentSessionIsBulk: bulk,
        currentSessionCount: 0,
        currentBallot: {},
        currentBallotReadyToSubmit: false,
        recording: true
      },
      () => {
        this.updateNextFilm();
      }
    );
    if (!bulk) {
      window.addEventListener("keyup", this.onKeypress);
    }
  }

  close() {
    this.setState({ recording: false });
    BallotActions.fetchStats.defer(this.props.eventBucket.id); // update
    window.removeEventListener("keyup", this.onKeypress);
  }

  componentWillReceiveProps(props) {
    if (
      this.state.recording &&
      this.state.currentSessionIsBulk &&
      !this.state.submitting
    ) {
      this.setState({
        currentBallot: props.getVotesForEvent(this.state.event.id) || {}
      });
    }
  }

  componentWillUnmount() {
    this.close();
  }

  render() {
    let { eventBucket } = this.props,
      { event, submitting } = this.state,
      styles = this.getStyles(),
      isReversed = false,
      labels = this.props.settings.rank_labels.split(",").map(l => l.trim());

    // numeric = worst->best
    if (labels.some(l => +l === 1)) {
      isReversed = true;
      labels.reverse();
    }

    return (
      <div>
        <Dialog
          title="Ballot totals"
          modal={false}
          open={this.state.totalsShown}
          bodyStyle={{ overflowY: "scroll" }}
          actions={[
            <FlatButton
              label="Close"
              primary={true}
              onClick={() => this.setState({ totalsShown: false })}
            />
          ]}
          onRequestClose={() => this.setState({ totalsShown: false })}
        >
          {this.state.totalsShown ? (
            <div>
              <p>These totals include both paper & app-submitted ballots.</p>
              <Table
                className={"bordered striped responsive-table"}
                columns={[
                  { key: "name", label: "Event" },
                  { key: "date", label: "Date" },
                  { key: "ballots", label: "Ballots recorded" }
                ]}
                data={EventStore.getEventsForEventBucket(eventBucket.id).map(
                  e => {
                    return {
                      name: e.name,
                      date: moment(e.start_time).format("lll"),
                      ballots:
                        (this.props.ballotStats &&
                          this.props.ballotStats.by_event[e.id]) ||
                        "–"
                    };
                  }
                )}
              />
            </div>
          ) : null}
        </Dialog>
        <BaseOverlay
          useMuiDefaultStyle={true}
          title={
            <div>
              {event &&
                `${event.name} – ${moment(event.start_time).format("lll")}`}
              <div style={styles.count}>
                {this.state.currentSessionCount} ballot
                {this.state.currentSessionCount !== 1 ? "s" : ""} recorded this
                session so far
                {event && this.props.ballotStats ? (
                  <span>
                    &nbsp;|{" "}
                    {(this.props.ballotStats.by_event[event.id] || 0) +
                      this.state.currentSessionCount}{" "}
                    total recorded for event
                  </span>
                ) : null}
              </div>
            </div>
          }
          modal={false}
          open={this.state.recording}
          actions={[
            <FlatButton
              label="Close ballot entry"
              primary={false}
              onClick={this.close}
            />
          ]}
        >
          {event ? (
            <div>
              {this.state.currentSessionIsBulk ? (
                <p>
                  Enter vote totals for each film on the ballot. You can adjust
                  these values after entering. The totals here{" "}
                  <strong>include all previously entered votes</strong>, so
                  editing them may effectively overwrite previous votes.
                </p>
              ) : (
                <p>
                  Use the number keys to enter each vote (1={labels[0]}, 2=
                  {labels[1]}, etc.), then press the Enter/Return key to submit.
                  <br />
                  Once a ballot has been submitted, it cannot be edited.
                </p>
              )}
              <div style={styles.previewBallot.root}>
                {event.films.length === 0 ? (
                  <div key={"none"} style={styles.previewBallot.entry}>
                    <div style={{ fontStyle: "italic" }}>
                      No balloted films to record
                    </div>
                  </div>
                ) : null}
                {generateGrid(event).map(row => {
                  return (
                    <div style={styles.previewBallot.row}>
                      {row.map(f => {
                        return (
                          <div key={f.id} style={styles.previewBallot.entry}>
                            <div style={styles.previewBallot.name}>
                              {f.name}
                            </div>
                            {f.unballoted ? (
                              <div>
                                <i>Not in competition</i>
                              </div>
                            ) : (
                              <div>
                                <div
                                  style={
                                    styles.previewBallot.ratingButtonContainer
                                  }
                                >
                                  {labels.map((l, i) => {
                                    // In the case where there is only a single label, just use whatever key is
                                    // on the ballot. This is necessary for cases where the vote keys have gotten
                                    // out of sync and a single item => 5, for example SIGGRAPH 2021.
                                    // E.g. given label=["star"], ballot[film] = { 5: 27 }, grabs [5] as the possible vote.
                                    // Ref: https://eventiveteam.slack.com/archives/C01DQN0LV9C/p1628775299002200
                                    const singleBallotWorkaround =
                                      labels.length === 1 &&
                                      this.state.currentBallot[f.id] &&
                                      Object.keys(
                                        this.state.currentBallot[f.id]
                                      );

                                    const vote =
                                      singleBallotWorkaround &&
                                      singleBallotWorkaround.length === 1
                                        ? singleBallotWorkaround[0]
                                        : isReversed
                                        ? labels.length - i
                                        : i + 1;

                                    if (this.state.currentSessionIsBulk) {
                                      return (
                                        <div
                                          key={`${f.id}-${l}`}
                                          style={{
                                            display: "inline-block",
                                            marginLeft: 5
                                          }}
                                        >
                                          <strong>{l}:</strong>&nbsp;
                                          <TextField
                                            hintText={"0"}
                                            style={{ width: 30 }}
                                            value={
                                              (this.state.currentBallot[f.id] &&
                                                this.state.currentBallot[f.id][
                                                  vote
                                                ]) ||
                                              ""
                                            }
                                            onChange={e =>
                                              this.updateVote(f.id, {
                                                score: vote,
                                                count: +e.target.value
                                              })
                                            }
                                          />
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        key={`${f.id}-${l}`}
                                        style={Object.assign(
                                          {},
                                          styles.previewBallot.ratingBubble,
                                          this.state.currentBallot[f.id] ===
                                            vote &&
                                            styles.previewBallot
                                              .ratingBubbleSelected
                                        )}
                                        onClick={() =>
                                          this.updateVote(f.id, vote)
                                        }
                                      >
                                        {l}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {submitting && <div style={styles.previewBallot.submitting} />}
              </div>
              <div style={styles.submit}>
                <RaisedButton
                  primary
                  label={
                    this.state.currentSessionIsBulk
                      ? this.state.submitting
                        ? "Updating voting totals..."
                        : "Update vote totals"
                      : this.state.submitting
                      ? "Recording ballot..."
                      : "Record ballot"
                  }
                  disabled={
                    this.state.submitting ||
                    !this.state.currentBallotReadyToSubmit
                  }
                  onClick={this.submitBallot}
                />
              </div>
            </div>
          ) : null}
        </BaseOverlay>
        <p>
          Use this interface to record paper ballot votes.{" "}
          <i>Note: App-based votes are already recorded automatically.</i>
          <br />
          To see which events may have yet to be recorded, review the{" "}
          <LinkLike onTouchTap={() => this.setState({ totalsShown: true })}>
            number of ballots recorded per event
          </LinkLike>
          .
        </p>
        <InfoNotice style={{ marginBottom: "1em", fontSize: "10pt" }}>
          Check out our{" "}
          <a
            href="https://help.eventive.org/eventive-balloting/recording-paper-votes"
            target="_blank"
          >
            guide to recording paper votes
          </a>
          .
        </InfoNotice>
        <div style={{ maxWidth: 600 }}>
          <InjectedSelect
            value={event && event.id}
            label="Event to record"
            labelFormatter={e =>
              `${e.name} – ${moment(e.start_time).format("lll")}`
            }
            type="events"
            eventBucketId={eventBucket.id}
            changeValueFunction={e => e}
            onChange={v => this.setState({ event: v })}
          />
        </div>
        <div style={styles.actions}>
          <RaisedButton
            primary
            disabled={!event}
            label="Launch ballot entry"
            onClick={() => this.launch()}
          />
          <RaisedButton
            primary
            disabled={!event}
            label="Bulk entry"
            style={{ marginLeft: 7 }}
            onClick={() => this.launch(true)}
          />
        </div>
      </div>
    );
  }
}

export default Radium(BallotingRecord);



// WEBPACK FOOTER //
// ./src/app/components/balloting-record.jsx